// Aba "ARTs" do detalhe do contrato — lista cards de cada ART + ações
// (editar, duplicar, copiar pro CREA, excluir).
// Wave 2 (Antd) — 2026-06-01. ModalArt continua legacy (Wave 3 dedicada).

import { useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Tag, Typography, Space, Flex, Empty, Popconfirm,
  App as AntApp,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined,
  FileTextOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import type { Contrato, ArtFull } from './types';
import { STATUS_CREA_VIS, VIGENCIA_VIS } from './types';
import { fmt, fmtData, getToken, statusArtVigencia } from './utils';
import { ModalArt } from './ModalArt';
import { ModalArtCopiar } from './ModalArtCopiar';

export function AbaArts({ contrato, onAtualizar }: { contrato: Contrato; onAtualizar: () => void }) {
  return <AntApp><AbaArtsInner contrato={contrato} onAtualizar={onAtualizar} /></AntApp>;
}

function AbaArtsInner({ contrato, onAtualizar }: { contrato: Contrato; onAtualizar: () => void }) {
  const { message } = AntApp.useApp();
  const arts = contrato.arts || [];
  const [editando, setEditando] = useState<string | 'nova' | null>(null);
  const [duplicandoDe, setDuplicandoDe] = useState<ArtFull | null>(null);
  const [copiarPraCrea, setCopiarPraCrea] = useState<ArtFull | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);

  function duplicarArt(orig: ArtFull) {
    setDuplicandoDe(orig);
    setEditando('nova');
  }

  async function abrirAnexo(s3_key: string) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_download_url_art', contrato_id: contrato.id, s3_key },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      if (d.url) window.open(d.url, '_blank');
      else message.warning('Não foi possível gerar URL de download.');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function excluirArt(art: NonNullable<Contrato['arts']>[number]) {
    setExcluindo(art.id);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_excluir_art', id: art.id },
        { headers: { Authorization: token } });
      message.success('ART excluída.');
      onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setExcluindo(null); }
  }

  return (
    <div>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 14 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          ARTs registradas — vínculo CREA com responsável técnico, datas, atividades técnicas/específicas e PDFs.
        </Typography.Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditando('nova')}>
          Nova ART
        </Button>
      </Flex>

      {arts.length === 0 ? (
        <Empty description='Nenhuma ART registrada. Clique em "+ Nova ART" pra começar.' />
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {arts.map(a => {
            const stCrea = a.status_crea || 'rascunho_interno';
            const visCrea = STATUS_CREA_VIS[stCrea] || STATUS_CREA_VIS.rascunho_interno;
            const stVig = statusArtVigencia(a);
            const visVig = VIGENCIA_VIS[stVig];
            const totLinhas = a.atividades_linhas?.length || 0;
            const totLegacy = (a.cat_tecnicas?.length || 0) + (a.cat_especificas?.length || 0);
            const enderecoStr = a.obra_logradouro
              ? `${a.obra_logradouro}${a.obra_numero ? ', ' + a.obra_numero : ''}${a.obra_bairro ? ' — ' + a.obra_bairro : ''}${a.obra_municipio ? ', ' + a.obra_municipio : ''}${a.obra_uf ? '/' + a.obra_uf : ''}`
              : a.endereco_obra;
            return (
              <Card key={a.id} size="small" styles={{ body: { padding: '12px 14px' } }}>
                <Flex gap={10} align="start">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Cabeçalho */}
                    <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 6 }}>
                      <Typography.Text strong style={{ fontFamily: 'monospace', fontSize: 13, color: '#020D7F' }}>
                        {a.numero || <Typography.Text type="secondary">(sem número CREA)</Typography.Text>}
                      </Typography.Text>
                      <Tag style={{ background: visCrea.bg, color: visCrea.cor, fontWeight: 600 }}>
                        {visCrea.label}
                      </Tag>
                      {a.assinado_govbr && (
                        <Tag color="green" icon={<CheckCircleOutlined />}>Assinado Gov.br</Tag>
                      )}
                      {a.tipo_art && (
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{a.tipo_art}</Typography.Text>
                      )}
                      {(a.inicio_art || a.fim_art) && (
                        <Typography.Text style={{ fontSize: 11, color: visVig.cor }}>
                          {fmtData(a.inicio_art)} → {fmtData(a.fim_art)} <strong>({visVig.label})</strong>
                        </Typography.Text>
                      )}
                      {a.valor != null && (
                        <Typography.Text strong style={{ fontSize: 11, color: '#0a7f4a' }}>
                          {fmt(a.valor)}
                        </Typography.Text>
                      )}
                    </Flex>

                    {/* RT */}
                    <Typography.Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>
                      <Typography.Text type="secondary">RT:</Typography.Text>{' '}
                      {a.rt_nome ? <strong>{a.rt_nome}</strong> : <Typography.Text type="secondary">—</Typography.Text>}
                      {a.rt_carteira && <Typography.Text type="secondary"> · {a.rt_carteira}</Typography.Text>}
                      {a.rt_titulo_profissional && <Typography.Text type="secondary"> · {a.rt_titulo_profissional}</Typography.Text>}
                    </Typography.Text>

                    {(a.proprietario_nome || a.contratante_nome) && (
                      <Typography.Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>
                        {a.contratante_nome && (
                          <><Typography.Text type="secondary">Contratante:</Typography.Text> {a.contratante_nome}</>
                        )}
                        {a.contratante_nome && a.proprietario_nome && a.proprietario_cliente_id !== a.contratante_cliente_id && <Typography.Text type="secondary"> · </Typography.Text>}
                        {a.proprietario_nome && a.proprietario_cliente_id !== a.contratante_cliente_id && (
                          <><Typography.Text type="secondary">Proprietário:</Typography.Text> {a.proprietario_nome}</>
                        )}
                      </Typography.Text>
                    )}

                    {/* Atividades (linhas ou legacy) */}
                    {totLinhas > 0 ? (
                      <Typography.Text style={{ fontSize: 11, color: '#555', display: 'block', marginTop: 6 }}>
                        <Typography.Text type="secondary">📋 Atividades:</Typography.Text>{' '}
                        <strong>{totLinhas}</strong> linha{totLinhas > 1 ? 's' : ''}
                        {a.atividades_linhas.slice(0, 2).map((l, i) => (
                          <Typography.Text key={i} type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>
                            • {l.cat_tecnica_nome || '?'} → {l.cat_especifica_nome || '?'} ({l.quantidade} {l.unidade})
                          </Typography.Text>
                        ))}
                        {totLinhas > 2 && <Typography.Text type="secondary" style={{ marginLeft: 6 }}>+{totLinhas - 2} mais</Typography.Text>}
                      </Typography.Text>
                    ) : totLegacy > 0 ? (
                      <Space size={4} wrap style={{ marginTop: 6 }}>
                        <Tag color="orange">⚠ Atividades legacy (Notion)</Tag>
                        {a.cat_tecnicas?.slice(0, 4).map(t => <Tag key={'t' + t.id} color="blue">{t.nome}</Tag>)}
                        {(a.cat_tecnicas?.length || 0) > 4 && (
                          <Typography.Text type="secondary" style={{ fontSize: 10 }}>+{(a.cat_tecnicas?.length || 0) - 4}</Typography.Text>
                        )}
                        {a.cat_especificas?.slice(0, 3).map(e => <Tag key={'e' + e.id} color="purple">{e.nome}</Tag>)}
                      </Space>
                    ) : null}

                    {enderecoStr && (
                      <Typography.Text style={{ fontSize: 11, color: '#555', display: 'block', marginTop: 6 }}>
                        <Typography.Text type="secondary">📍</Typography.Text> {enderecoStr}
                      </Typography.Text>
                    )}
                    {a.finalidade && a.finalidade !== 'Outras Finalidades' && (
                      <Typography.Text style={{ fontSize: 11, color: '#555', display: 'block', marginTop: 4 }}>
                        <Typography.Text type="secondary">Finalidade:</Typography.Text> {a.finalidade}
                      </Typography.Text>
                    )}

                    {/* Anexos */}
                    {(((a.rascunho_s3_keys?.length || 0) + (a.assinada_s3_keys?.length || 0) +
                       (a.rascunho_notion_urls?.length || 0) + (a.assinada_notion_urls?.length || 0)) > 0) && (
                      <Space size={6} wrap style={{ marginTop: 8 }}>
                        {(a.rascunho_s3_keys || []).map((k, i) => (
                          <Button key={'r' + i} size="small" icon={<FileTextOutlined />}
                            onClick={() => abrirAnexo(k)}>Rascunho {i + 1}</Button>
                        ))}
                        {(a.assinada_s3_keys || []).map((k, i) => (
                          <Button key={'s' + i} size="small" icon={<CheckCircleOutlined />}
                            onClick={() => abrirAnexo(k)}
                            style={{ borderColor: '#B5DCC4', color: '#0a7f4a', fontWeight: 600 }}>
                            ART assinada {i + 1}
                          </Button>
                        ))}
                        {(a.rascunho_notion_urls || []).map((u, i) => (
                          <Button key={'rn' + i} size="small" icon={<FileTextOutlined />}
                            href={u.url} target="_blank">
                            {u.nome || 'Rascunho Notion ' + (i + 1)}
                          </Button>
                        ))}
                        {(a.assinada_notion_urls || []).map((u, i) => (
                          <Button key={'sn' + i} size="small" icon={<CheckCircleOutlined />}
                            href={u.url} target="_blank"
                            style={{ borderColor: '#B5DCC4', color: '#0a7f4a', fontWeight: 600 }}>
                            {u.nome || 'Assinada Notion ' + (i + 1)}
                          </Button>
                        ))}
                      </Space>
                    )}
                  </div>

                  {/* Ações */}
                  <Space direction="vertical" size={4}>
                    <Button size="small" onClick={() => setCopiarPraCrea(a)}
                      icon={<CopyOutlined />}
                      style={{ borderColor: '#0a7f4a', color: '#0a7f4a' }}
                      title="Copiar pro CREA campo-a-campo">
                      Copiar pro CREA
                    </Button>
                    <Button size="small" onClick={() => duplicarArt(a)}
                      style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                      title="Duplicar (útil pra trocar o RT)">
                      ⎘ Duplicar
                    </Button>
                    <Button size="small" type="primary" ghost icon={<EditOutlined />}
                      onClick={() => setEditando(a.id)}>Editar</Button>
                    <Popconfirm title={`Excluir ART${a.numero ? ' ' + a.numero : ''}?`}
                      okText="Excluir" okButtonProps={{ danger: true }}
                      onConfirm={() => excluirArt(a)}>
                      <Button size="small" danger icon={<DeleteOutlined />}
                        loading={excluindo === a.id} />
                    </Popconfirm>
                  </Space>
                </Flex>
              </Card>
            );
          })}
        </Space>
      )}

      {/* Modal nova/editar (legacy — Wave 3) */}
      {editando && (
        <ModalArt
          contrato={contrato}
          art={editando === 'nova' ? null : (arts.find(a => a.id === editando) || null)}
          duplicarDe={editando === 'nova' ? duplicandoDe : null}
          onClose={() => { setEditando(null); setDuplicandoDe(null); }}
          onSucesso={() => { setEditando(null); setDuplicandoDe(null); onAtualizar(); }}
        />
      )}

      {copiarPraCrea && (
        <ModalArtCopiar art={copiarPraCrea} contrato={contrato}
          onClose={() => setCopiarPraCrea(null)} />
      )}
    </div>
  );
}
